// LivePreview (board 25 v2 §14.6): the public page's card as the visitor will
// see it — cover (the merchant's cover later; teal-light until then), the
// letter tile, the name, «الفئة · المدينة · الفرع · العنوان», the hours and phone
// chips, the shape line (the team) and the «احجز» button. Grey skeleton bars
// where an answer is still missing; the «منشورة» badge on the done screen.

import { CalendarCheck, Check, Clock, Phone, Users } from 'lucide-react';
import { useState, type CSSProperties } from 'react';
import { DONE } from '../copy';

export interface PreviewData {
  name?: string;
  cat?: string;
  city?: string;
  /** The first branch's name, after the city («عيادة · بغداد · المنصور», board 26). */
  branch?: string;
  addr?: string;
  /** '0770 123 4567' (rendered LTR). */
  phone?: string;
  /** «السبت – الخميس · ٤:٠٠ م – ٩:٠٠ م» */
  hours?: string;
  /** The shape line («الفريق: …», «أنت ضمن الفريق · موظف»). */
  line?: string;
  lineIcon?: 'users' | 'calendar';
  cta?: string;
  slug?: string | null;
  /** The name types in when it is already complete as the preview opens (the business name step only; board 25 v2 `typeIn`). */
  typeName?: boolean;
  /** No floating motion (the done screen on a phone). */
  still?: boolean;
}

function Bar({ w, h = 12 }: { w: number; h?: number }) {
  return <span className="onb-sk" style={{ width: w, height: h }} aria-hidden="true" />;
}

/** The name line. On the name step, when it arrives already complete (a restored draft, a back-navigation)
 *  it types in (board 25 v2 `typeIn`, one step per character); while the visitor is typing it grows with
 *  each key, so a first character never replays the animation. Every other step shows it plain. */
function PreviewName({ name, typeIn }: { name: string; typeIn?: boolean }) {
  const [typed] = useState(() => !!typeIn && [...name].length > 1);
  return (
    <div
      className={`onb-preview-name${typed ? ' onb-preview-name--type' : ''}`}
      style={typed ? ({ '--chars': [...name].length } as CSSProperties) : undefined}
      data-testid="onb-preview-name"
    >
      {name}
    </div>
  );
}

export function LivePreview({ data, done, width = 380, cover = 140 }: { data: PreviewData; done?: boolean; width?: number; cover?: number }) {
  const meta = [data.cat, data.city, data.branch, data.addr].filter((x, i, all) => !!x && all.indexOf(x) === i).join(' · ');
  const initial = data.name?.trim() ? data.name.trim()[0] : '·';
  const LineIcon = data.lineIcon === 'users' ? Users : CalendarCheck;
  return (
    <div className={`onb-preview${data.still ? ' onb-preview--still' : ''}`} style={{ width }} data-testid="onb-preview" data-done={done ? 'true' : undefined}>
      <div className="onb-preview-cover" style={{ height: cover }}>
        {done && (
          <span className="onb-preview-published" data-testid="onb-published">
            <Check aria-hidden="true" />
            {DONE.published}
          </span>
        )}
      </div>
      <div className="onb-preview-body">
        <div className="onb-preview-id">
          <span className="onb-preview-tile" aria-hidden="true">
            {initial}
          </span>
          <div className="onb-preview-text">
            {data.name?.trim() ? (
              <PreviewName name={data.name.trim()} typeIn={data.typeName} />
            ) : (
              <div className="onb-preview-skl">
                <Bar w={170} h={16} />
              </div>
            )}
            {meta ? (
              <div className="onb-preview-meta" data-testid="onb-preview-meta">
                {meta}
              </div>
            ) : (
              <div className="onb-preview-skl onb-preview-skl--meta">
                <Bar w={120} />
              </div>
            )}
          </div>
        </div>
        <div className="onb-preview-chips">
          {data.hours || data.phone ? (
            <>
              {data.hours && (
                <span className="onb-preview-chip" data-testid="onb-preview-hours">
                  <Clock aria-hidden="true" />
                  {data.hours}
                </span>
              )}
              {data.phone && (
                <span className="onb-preview-chip" data-testid="onb-preview-phone">
                  <Phone aria-hidden="true" />
                  <bdi dir="ltr" className="ltr">
                    {data.phone}
                  </bdi>
                </span>
              )}
            </>
          ) : (
            <>
              <Bar w={150} h={26} />
              <Bar w={110} h={26} />
            </>
          )}
        </div>
        {data.line && (
          <div className="onb-preview-line" data-testid="onb-preview-line">
            <LineIcon aria-hidden="true" />
            {data.line}
          </div>
        )}
        <div className="onb-preview-cta" aria-hidden="true">
          {data.cta || DONE.cta}
        </div>
      </div>
    </div>
  );
}
